import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useDevStore } from '@/store/pinia/dev'
import { useOrganizationStore } from '@/store/pinia/organization'
import { parseAggregateData } from '@/utils/api/parsers/cloud-cost-management/aggregateData'
import { parseProviderHistograms, aggregateMultipleProviderData } from '@/utils/api/parsers/cloud-cost-management/providerHistograms'
import { parseTopProjects } from '@/utils/api/parsers/cloud-cost-management/topProjects'
import { providersExtraInfo, buildFilterQueryString, parseHistogramDates, getAccountStatus } from '@/utils/helpers/cloud-cost-management'
import {
  makeClearErrorsAction,
  makeResetStoreAction,
  makeSetErrorsAction,
  makeStartFetchAction,
  makeStopFetchAction,
} from '@/utils/helpers/pinia'
import { $date } from '@/utils/plugins/date-fns'
import i18n from '@/utils/plugins/i18n'

export const persistedQueryFields = {
  dashboard: ['begin', 'end', 'currency', 'granularity'],
  providerDetail: ['begin', 'end', 'currency', 'granularity', 'group_by', 'projects'],
}

export const state = () => ({
  accounts: [],
  aggregateData: {},
  credential: null,
  errors: {
    accounts: null,
    dashboard: null,
    gcpBillingDataset: null,
    providerData: null,
    tagMapping: null,
  },
  fetchInProgress: {
    providerData: false,
  },
  filterValues: {},
  graphOrderingParam: 'cost',
  graphTypes: {
    providerDetail: 'line',
    topProjects: 'line',
  },
  histogramDates: [],
  providerData: [],
  queryBody: {
    begin: $date.format($date.subDays(Date.now(), 7), 'yyyy-MM-dd'),
    end: $date.format(Date.now(), 'yyyy-MM-dd'),
    currency: 'EUR',
    granularity: 'day',
    group_by: [null, null],
  },
  tagMapping: null,
  tagMappingTags: [],
  topProjects: {
    histograms: {},
    resources: [],
  },
})

export const useCloudCostManagementStore = defineStore('cloudCostManagement', {
  state,
  getters: {
    getProviderExtraInfo: () => providersExtraInfo,
    getProviderExtraInfoByCanonical: () => _.mapKeys(providersExtraInfo, ({ canonical }) => canonical),
    getProviderExtraInfoByCredType: () => _.mapKeys(providersExtraInfo, ({ credentialType }) => credentialType),
    getImportingAccounts: (state) => _.filter(state.accounts, ({ enabled, status }) => getAccountStatus(enabled, status) === 'importing'),
    getErroredAccounts: (state) => _.filter(state.accounts, ({ enabled, status }) => getAccountStatus(enabled, status) === 'errored'),
    hasTagMapping: (state) => {
      const {
        environment_tags: environmentTags,
        project_tags: projectTags,
        environment_regex: environmentRegex,
        project_regex: projectRegex,
      } = state.tagMapping || {}
      const environmentTagMappingIsDefined = !_.isEmpty(environmentTags) || environmentRegex
      const projectTagMappingIsDefined = !_.isEmpty(projectTags) || projectRegex
      return !!(environmentTagMappingIsDefined && projectTagMappingIsDefined)
    },
  },
  actions: {
    CLEAR_CE_ERRORS: makeClearErrorsAction(state),
    RESET_CE_STATE: makeResetStoreAction(state),
    SET_ERRORS: makeSetErrorsAction(),
    START_FETCH: makeStartFetchAction(),
    STOP_FETCH: makeStopFetchAction(),

    /**
     * Get all cloud accounts with finops enabled.
     * @returns {Promise<void>}
     */
    async GET_ACCOUNTS () {
      const devStore = useDevStore()
      if (!devStore.$showFeature.cloudEnvironments) return
      this.CLEAR_CE_ERRORS('accounts')
      const orgStore = useOrganizationStore()
      const { data, errors } = (await this.$cycloid.ydAPI.getCloudAccounts(orgStore.orgCanonical)) || {}
      if (data) this.accounts = data.filter(({ finops_account: finopsAccount }) => finopsAccount).map(({ finops_account: finopsAccount }) => finopsAccount)
      if (errors) this.SET_ERRORS({ key: 'accounts', errors })
    },

    /**
     * Pause cost ingestion for a cloud cost management account.
     * @param {Object} params
     * @param {string} params.accountCanonical - The canonical of the cloud account to pause finops for.
     * @returns {Promise<void>}
     */
    async PAUSE_ACCOUNT ({ accountCanonical }) {
      this.CLEAR_CE_ERRORS('accounts')
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()
      const { data, errors } = (await this.$cycloid.ydAPI.pauseCloudAccountFinops(orgStore.orgCanonical, accountCanonical)) || {}
      if (data) {
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.cloudCostManagement.account.paused') })
      }
      if (errors) this.SET_ERRORS({ key: 'accounts', errors })
    },

    /**
     * Unpause cost ingestion for a cloud cost management account.
     * @param {Object} params
     * @param {string} params.accountCanonical - The canonical of the cloud account to unpause finops for.
     * @returns {Promise<void>}
     */
    async UNPAUSE_ACCOUNT ({ accountCanonical }) {
      this.CLEAR_CE_ERRORS('accounts')
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()
      const { data, errors } = (await this.$cycloid.ydAPI.unpauseCloudAccountFinops(orgStore.orgCanonical, accountCanonical)) || {}
      if (data) {
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.cloudCostManagement.account.unpaused') })
      }
      if (errors) this.SET_ERRORS({ key: 'accounts', errors })
    },

    /**
     * Trigger an immediate cost ingestion for a cloud cost management account.
     * @param {Object} params
     * @param {string} params.accountCanonical - The canonical of the cloud account to ingest finops for.
     * @returns {Promise<void>}
     */
    async INGEST_ACCOUNT ({ accountCanonical }) {
      this.CLEAR_CE_ERRORS('accounts')
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()
      const { errors } = (await this.$cycloid.ydAPI.ingestCloudAccountFinops(orgStore.orgCanonical, accountCanonical)) || {}
      if (!errors) {
        alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.cloudCostManagement.account.ingesting') })
      }
      if (errors) this.SET_ERRORS({ key: 'accounts', errors })
    },

    /**
     * Get the cloud cost management tag mapping.
     * @returns {Promise<void>}
     */
    async GET_TAG_MAPPING () {
      this.CLEAR_CE_ERRORS('tagMapping')
      const orgStore = useOrganizationStore()
      const { data, errors } = (await this.$cycloid.ydAPI.getCloudCostManagementTagMapping(orgStore.orgCanonical)) || {}
      if (data) {
        this.tagMapping = data
        this.tagMappingTags = data.all_tags
      }
      if (errors) this.SET_ERRORS({ key: 'tagMapping', errors })
    },

    /**
     * Fetch the cloud cost management dashboard data.
     * @param {Object} params - The params to fetch the dashboard data.
     * @param {import('vue-router').Router} params.router - The router instance.
     * @param {boolean} [params.persistQuery=true] - Whether to persist the query in the URL.
     * @returns {Promise<void>}
     */
    async FETCH_DASHBOARD_DATA ({ router, persistQuery = true }) {
      if (persistQuery) this.UPDATE_URL_QUERY(router)
      this.CLEAR_CE_ERRORS('dashboard')
      const orgStore = useOrganizationStore()
      const queryString = buildFilterQueryString(this.queryBody)
      const { data, errors } = (await this.$cycloid.ydAPI.getCloudCostManagementDashboard(orgStore.orgCanonical, queryString)) || {}
      if (data) {
        const histogramDates = parseHistogramDates(this.queryBody)
        this.topProjects = parseTopProjects(data, histogramDates)
        this.aggregateData = parseAggregateData(data, this.queryBody.granularity)
        this.histogramDates = histogramDates
        this.filterValues = data?.filter_values || {}
      }
      if (errors) this.SET_ERRORS({ key: 'dashboard', errors })
    },

    /**
     * Fetch the cloud cost management provider data.
     * @param {Object} params - The params to fetch the provider data.
     * @param {string} params.providerCanonical - The canonical of the provider to fetch the data from.
     * @param {import('vue-router').Router} params.router - The router instance.
     * @returns {Promise<void>}
     */
    async FETCH_PROVIDER_DATA ({ providerCanonical, router }) {
      this.UPDATE_URL_QUERY(router)
      this.CLEAR_CE_ERRORS('providerData')
      const orgStore = useOrganizationStore()
      const queryString = buildFilterQueryString(this.queryBody)
      const { data, errors } = (await this.$cycloid.ydAPI.getCloudCostManagementProvider(orgStore.orgCanonical, providerCanonical, queryString)) || {}
      if (data) {
        this.providerData = parseProviderHistograms(data.cost_histogram)
        this.histogramDates = parseHistogramDates(this.queryBody)
        this.filterValues = data?.filter_values || {}
      }
      if (errors) this.SET_ERRORS({ key: 'providerData', errors })
    },

    /**
     * Fetch the cloud cost management multiple provider data.
     * @param {Object} params - The params to fetch the multiple provider data.
     * @param {Array<string>} params.providers - The list of providers to fetch the data from.
     * @returns {Promise<void>}
     */
    async FETCH_MULTIPLE_PROVIDER_DATA ({ providers }) {
      this.START_FETCH('providerData')
      this.CLEAR_CE_ERRORS('providerData')
      const orgStore = useOrganizationStore()
      const queryString = buildFilterQueryString(this.queryBody)
      const responses = await Promise.all(
        providers.map((provider) =>
          this.$cycloid.ydAPI.getCloudCostManagementProvider(orgStore.orgCanonical, provider, queryString),
        ),
      )
      const providersData = []
      responses.forEach(({ data, errors }) => {
        if (data) providersData.push(parseProviderHistograms(data.cost_histogram))
        if (errors) this.SET_ERRORS({ key: 'providerData', errors })
      })
      const aggregatedData = aggregateMultipleProviderData(providersData)
      this.providerData = aggregatedData
      this.STOP_FETCH('providerData')
    },

    /**
     * Update the cloud cost management tag mapping.
     * @param {Object} params - The params to update the tag mapping.
     * @param {Object} params.tagMapping - The tag mapping to update.
     * @returns {Promise<void>}
     */
    async UPDATE_TAG_MAPPING ({ tagMapping }) {
      this.CLEAR_CE_ERRORS('tagMapping')
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()
      const { data, errors } = (await this.$cycloid.ydAPI.updateCloudCostManagementTagMapping(orgStore.orgCanonical, tagMapping)) || {}
      if (errors) this.SET_ERRORS({ key: 'tagMapping', errors })
      if (data) alertStore.SHOW_ALERT({ type: 'success', content: i18n.global.t('alerts.success.cloudCostManagement.tagMapping.updated') })
    },

    /**
     * Read the query from the URL and set it in the state.
     * @returns {void}
     */
    READ_URL_QUERY () {
      const urlQueryParams = new URLSearchParams(window.location.search)
      const queryString = urlQueryParams.get('filters')
      this.queryBody = JSON.parse(queryString) || state().queryBody
    },

    /**
     * Update the URL with the current query.
     * @param {import('vue-router').Router} router - The router instance.
     * @returns {void}
     */
    UPDATE_URL_QUERY (router) {
      const filters = JSON.stringify(this.queryBody)
      const urlQueryParams = new URLSearchParams(window.location.search)
      urlQueryParams.set('filters', filters)
      // https://router.vuejs.org/guide/migration/#Usage-of-history-state
      history.replaceState(history.state, null, `?${urlQueryParams.toString()}`)
      router.replace({ query: { filters } }).catch(() => { /* silenced */ })
    },

    /**
     * Set data in the cloud cost management state.
     * @param {Object} params - The params to set the data.
     * @param {string} params.key - The key of the data to set.
     * @param {*} params.data - The data to set.
     * @deprecated use direct assignment instead
     * @returns {void}
     */
    SET_CE_DATA ({ key, data }) {
      this[key] = data
    },

    /**
     * Set the graph ordering param.
     * @param {string} value - The value to set.
     * @returns {void}
     */
    SET_GRAPH_ORDERING_PARAM (value) {
      this.graphOrderingParam = value
    },

    /**
     * Set the graph type.
     * @param {Object} params - The params to set the graph type.
     * @param {string} params.type - The type of the graph to set.
     * @param {string} params.value - The value to set.
     * @returns {void}
     */
    SET_GRAPH_TYPE ({ type, value }) {
      this.graphTypes = { ...this.graphTypes, [type]: value }
      localStorage.setItem(LSK.CCM_GRAPHS_STATE, JSON.stringify(this.graphTypes))
    },

    /**
     * Set the filter values.
     * @param {Object} values - The values to set.
     * @returns {void}
     */
    SET_FILTER_VALUES (values) {
      this.filterValues = values
    },

    /**
     * Set data in the tag mapping.
     * @param {Object} params - The params to set the data.
     * @param {string} params.key - The key of the data to set.
     * @param {*} params.data - The data to set.
     * @returns {void}
     */
    SET_TAG_MAPPING ({ key, data }) {
      this.tagMapping = { ...this.tagMapping, [key]: data }
    },

    /**
     * Set a query filter.
     * @param {Object} params - The params to set the filter.
     * @param {string} params.key - The key of the filter to set.
     * @param {*} params.value - The value to set.
     * @returns {void}
     */
    SET_QUERY_FILTER ({ key, value }) {
      let filterValue = value
      if (_.isEmpty(value)) {
        this.queryBody = _.omit(this.queryBody, key)
        return
      }
      // Correctly sends to BE "" when "No project" value is selected
      if (key === 'projects' && _.includes(value, i18n.global.t('cloudCostManagement.noProject'))) {
        filterValue = [..._.without(value, i18n.global.t('cloudCostManagement.noProject')), '']
      }
      this.queryBody = { ...this.queryBody, [key]: filterValue }
    },

    /**
     * Reset the query body.
     * @param {string} [page='dashboard'] - The page to reset the query body for.
     * @returns {void}
     */
    RESET_QUERY_BODY (page = 'dashboard') {
      const persistedValues = _.pick(this.queryBody, persistedQueryFields[page])
      this.queryBody = { ...state().queryBody, ...persistedValues }
    },
  },
})
